import Layout from "../Layout";
import { useForm } from "@inertiajs/react";
import { Input } from "@/components/ui/input";
import { FormButton } from "@/components/ui/form-button";
import { Building2, User } from "lucide-react";
import { FormField } from "@/components/ui/form_field";
import { getFormIconClassName } from "@/lib/utils";

export default function Setup() {
  const { data, setData, post, processing, errors } = useForm({
    workspace_type: "personal",
    organization: {
      name: "",
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    post("/workspaces");
  }

  return (
    <Layout
      title="Set up your Workspace"
      description="Choose how you'd like to start with Infralyst. You can change this later."
    >
      <section className="px-4 py-16 pt-40">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="mb-4 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight">
            Set up your <span className="text-primary">Workspace</span>
          </h1>
          <p className="max-w-2xl mx-auto text-xl leading-relaxed text-surface-foreground/80 mb-12">
            Choose how you'd like to start. You can change this later.
          </p>

          <form
            onSubmit={handleSubmit}
            className="max-w-xl mx-auto p-8 border shadow-lg rounded-2xl bg-background/70 dark:bg-zinc-900/80 backdrop-blur space-y-6"
          >

            {/* Workspace Type Selection */}
            <FormField as="fieldset" label="Workspace type">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label
                  className={`p-4 md:p-5 border-2 rounded-xl shadow-md transition-colors cursor-pointer ${
                    data.workspace_type === "personal"
                      ? "border-primary ring-2 ring-violet-200/60 dark:ring-violet-900/40 bg-violet-50/50 dark:bg-violet-950/30"
                      : "bg-white/80 dark:bg-zinc-900/80 border-gray-300 dark:border-zinc-700 hover:border-violet-300 dark:hover:border-violet-600"
                  }`}
                >
                  <input
                    type="radio"
                    id="workspace_type_personal"
                    name="workspace_type"
                    value="personal"
                    checked={data.workspace_type === "personal"}
                    onChange={(e) =>
                      setData(
                        "workspace_type",
                        e.target.value as "personal" | "team",
                      )
                    }
                    className="sr-only"
                    aria-describedby="workspace-help-text"
                  />
                  <div className="flex flex-col items-center space-y-2">
                    <User className="w-6 h-6 text-primary" />
                    <span className="font-medium">Personal</span>
                    <span className="text-sm text-surface-foreground/70">
                      Private to you. Great for trying Infralyst.
                    </span>
                  </div>
                </label>

                <label
                  className={`p-4 md:p-5 border-2 rounded-xl shadow-md transition-colors cursor-pointer ${
                    data.workspace_type === "team"
                      ? "border-primary ring-2 ring-violet-200/60 dark:ring-violet-900/40 bg-violet-50/50 dark:bg-violet-950/30"
                      : "bg-white/80 dark:bg-zinc-900/80 border-gray-300 dark:border-zinc-700 hover:border-violet-300 dark:hover:border-violet-600"
                  }`}
                >
                  <input
                    type="radio"
                    id="workspace_type_team"
                    name="workspace_type"
                    value="team"
                    checked={data.workspace_type === "team"}
                    onChange={(e) =>
                      setData(
                        "workspace_type",
                        e.target.value as "personal" | "team",
                      )
                    }
                    className="sr-only"
                    aria-describedby="workspace-help-text"
                  />
                  <div className="flex flex-col items-center space-y-2">
                    <Building2 className="w-6 h-6 text-primary" />
                    <span className="font-medium">Team</span>
                    <span className="text-sm text-surface-foreground/70">
                      Shared billing & repos.
                    </span>
                  </div>
                </label>
              </div>
              <p
                id="workspace-help-text"
                className="text-sm text-surface-foreground/70 text-center"
              >
                {data.workspace_type === "personal"
                  ? "You can convert to a Team workspace anytime."
                  : "You'll invite teammates after setup."}
              </p>
            </FormField>

            {/* Team Organization Name */}
            {data.workspace_type === "team" && (
              <div className="space-y-4">
                <FormField
                  id="organization_name"
                  label="Workspace name"
                  error={errors["organization.name"]}
                >
                  <Input
                    id="organization_name"
                    value={data.organization.name}
                    onChange={(e) =>
                      setData("organization", { name: e.target.value })
                    }
                    required={data.workspace_type === "team"}
                    placeholder="e.g. Acme Inc."
                    aria-invalid={!!errors["organization.name"]}
                    leftIcon={
                      <Building2
                        className={getFormIconClassName(
                          !!errors["organization.name"],
                        )}
                      />
                    }
                  />
                </FormField>
              </div>
            )}

            <FormButton
              type="submit"
              isLoading={processing}
              loadingText="Setting up..."
            >
              {data.workspace_type === "personal"
                ? "Create personal workspace"
                : "Create team workspace"}
            </FormButton>
          </form>
        </div>
      </section>
    </Layout>
  );
}